import React, {useCallback, SyntheticEvent} from 'react';
import {
	Avatar, CallIcon, Layout, MobilesIcon, Text, Divider, Radio,
} from '@phonectas/unifon-ui-kit';
import {useTranslation} from 'react-i18next';
import {LANG_LS_KEY} from 'common/i18n';

import {phoneNumber} from 'utils/number';
import UserModel from 'common/models/user';
import {StyledNoSelectText} from '../../styled';
import {UserInfoInterface} from 'common/types/store/userTypes';
import {THREE_SIXTY_ROLES} from 'common/constants';

interface UserInfoProps {
	data: UserInfoInterface | null;
}

const UserInfo: React.FC<UserInfoProps> = ({data}) => {
	const [t, i18n] = useTranslation();
	const onChangeLanguage = useCallback(async (e: SyntheticEvent<HTMLInputElement>) => {
		const newLanguage = e.currentTarget.value;
		await i18n.changeLanguage(newLanguage);
		try {
			localStorage.setItem(LANG_LS_KEY, newLanguage);
		} catch (err) {
			console.error(err);
		}
	}, [i18n]);

	const getThreeSixtyRoles = useCallback(() => {
		const threeSixtyRoleValues = Object.values(THREE_SIXTY_ROLES);
		const filteredRoles = data?.roles?.filter((role: string) =>
			threeSixtyRoleValues.includes(role)
		);
		if (!filteredRoles || filteredRoles.length === 0) {
			return (
				<StyledNoSelectText color="textSecondary" variant="body1">
					{t('No Unifon 360 roles assigned')}
				</StyledNoSelectText>
			);
		}
		return filteredRoles.map((role: string, index: number) => (
			<React.Fragment key={role}>
				<StyledNoSelectText color="textSecondary" variant="body1">
					{role}
				</StyledNoSelectText>
				{index < filteredRoles.length - 1 && (
					<StyledNoSelectText color="textSecondary" variant="body1">
						{' - '}
					</StyledNoSelectText>
				)}
			</React.Fragment>
		));
	}, [data?.roles, t]);

	const hasThreeSixtyRoles = useCallback(() => {
		const threeSixtyRoleValues = THREE_SIXTY_ROLES ? Object.values(THREE_SIXTY_ROLES) : [];
		return data?.roles?.some((role: string) => threeSixtyRoleValues.includes(role)) ?? false;
	}, [data?.roles]);

	return (
		<>
			<Layout
				nowrap
				fullWidth
				alignItems="start"
				justifyContent="space-between"
			>
				<Avatar
					size="huge"
					src={UserModel.getAvatarUrl(data?.id || '', data?.customerId || 0, data?.picture || '')}
				/>
				<Layout fullWidth direction="column" nowrap>
					<Layout gap="xs" direction="column">
						<Text variant="h1">{data?.name}</Text>
						<Layout gap="none" fullWidth nowrap alignItems="center" direction="column">
							{data?.mobile && (
								<Layout nowrap fullWidth key={data.mobile} alignItems="center">
									<MobilesIcon />
									<Text color="textSecondary" variant="body1">{phoneNumber(data?.mobile)}</Text>
								</Layout>
							)}
							{data?.fixed && (
								<Layout nowrap fullWidth key={data.fixed} alignItems="center">
									<CallIcon />
									<Text color="textSecondary" variant="body1">{phoneNumber(data?.fixed)}</Text>
								</Layout>
							)}
						</Layout>
						{data?.departmentName && (
							<Layout nowrap gap="xs" key={data?.departmentName}>
								<StyledNoSelectText color="textPrimary" variant="body1">{t('Department:')}</StyledNoSelectText>
								<Text color="textSecondary" variant="body1">{data?.departmentName}</Text>
							</Layout>
						)}
						{data?.email && (
							<Layout nowrap gap="xs" key={data?.email}>
								<StyledNoSelectText color="textPrimary" variant="body1">{t('Email:')}</StyledNoSelectText>
								<Text color="textSecondary" variant="body1">{data?.email}</Text>
							</Layout>
						)}
						<Layout direction="row" gap="xs" style={{alignItems: 'center'}}>
							<StyledNoSelectText color="textPrimary" variant="body1">
								{t('Roles:')}
							</StyledNoSelectText>
							{getThreeSixtyRoles()}
						</Layout>
						{!hasThreeSixtyRoles() && (
							<Text color="warning" variant="body1">
								{t('You do not have any Unifon 360 roles assigned. Please contact your administrator.')}
							</Text>
						)}
					</Layout>
					<Divider />
					<Text>{t('Language settings')}</Text>
					<Layout fullWidth nowrap>
						<Radio
							name="lang"
							value="en"
							label={t('English')}
							checked={i18n.language === 'en'}
							onChange={onChangeLanguage}
						/>
						<Radio
							name="lang"
							value="no"
							label={t('Norwegian')}
							checked={i18n.language === 'no'}
							onChange={onChangeLanguage}
						/>
					</Layout>
					<Divider />
				</Layout>
			</Layout>
			<Layout
				nowrap
				fullWidth
				alignItems="start"
				justifyContent="space-between"
			>
			</Layout>
		</>
	);
}

export default React.memo(UserInfo);